import React, { useState, useEffect, useRef } from 'react';
import { Parallax } from 'react-scroll-parallax';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import FloatingElements from './FloatingElements';

const HeroSection = styled.section`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const BackgroundSlider = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const BackgroundImage = styled(motion.div)`
  position: absolute;
  top: 130px;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => props.image});
  background-color: #4396CC;
  background-size: cover;
  background-position: center;
  
  &::after {
    content: '';
    position: absolute;
    
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    
    background: linear-gradient(
      100deg,
      rgba(102, 126, 234, 0) 0%,
      rgba(118, 75, 162, 0) 100%
    );
  }
`;

const Content = styled.div`
  text-align: center;
  z-index: 2;
  color: white;
  position: relative;
`;

const Title = styled(motion.h1)`
  font-family: 'BRLNSDB';
  font-size: 4rem;
  background: linear-gradient(to right, #f9ff31, #f9ff31);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  color: #f9ff31;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: rem;
  color: rgba(255, 255, 255, 0.9);
`;

const OwlWrapper = styled(motion.div)`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  transform-origin: bottom right;
`;

const FloatingShapes = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
`;

const SliderDots = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1rem;
  z-index: 3;
`;

const Dot = styled(motion.button)`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  background: ${props => props.active ? 'white' : 'transparent'};
  cursor: pointer;
  padding: 0;
  transition: all 0.1s ease;

  &:hover {
    transform: scale(1.2);
  }
`;

const HeroContainer = styled.div`
  font-family: "Montserrat";
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  gap: 20px;
`;

const HeroCharacter = styled(motion.div)`
  width: 110px;
  height: 110px;
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease;
  background: #4395CA;
  border-radius: 50%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: scale(1.1);
  }
`;

const OwlFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #4395CA 0%, #357abd 100%);
  border-radius: 50%;
  overflow: hidden;
`;

const OwlEyes = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
`;

const Eye = styled.div`
  width: 45px;
  height: 45px;
  background: white;
  border-radius: 50%;
  position: relative;
  overflow: hidden;
  border: 3px solid #000;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);

  &::before {
    content: '';
    position: absolute;
    top: 5px;
    left: 5px;
    width: 12px;
    height: 12px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
  }
`;

const Pupil = styled.div`
  width: 20px;
  height: 20px;
  background: #000;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid #fff;
`;

const OwlWings = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0;
`;

const Wing = styled.div`
  width: 55px;
  height: 85px;
  background: #4395CA;
  border-radius: 50% 50% 0 0;
  position: relative;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transform-origin: top center;
  animation: wingFlap 2s infinite ease-in-out;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 30px;
    height: 30px;
    background: #357abd;
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 25px;
    background: linear-gradient(to bottom, rgba(255,255,255,0.2), transparent);
    border-radius: 50% 50% 0 0;
  }

  @keyframes wingFlap {
    0%, 100% {
      transform: rotate(0deg);
    }
    50% {
      transform: rotate(5deg);
    }
  }
`;

const OwlBeak = styled.div`
  position: absolute;
  bottom: 30%;
  left: 50%;
  transform: translateX(-50%);
  width: 25px;
  height: 20px;
  background: #FFA500;
  clip-path: polygon(50% 100%, 0 0, 100% 0);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;

const OwlEars = styled.div`
  position: absolute;
  top: -15px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
`;

const Ear = styled.div`
  width: 20px;
  height: 35px;
  background: #4395CA;
  border-radius: 50% 50% 0 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 10px;
    height: 10px;
    background: #357abd;
    border-radius: 50%;
  }
`;

const OwlCheeks = styled.div`
  position: absolute;
  bottom: 35%;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 0 25px;
`;

const Cheek = styled.div`
  width: 15px;
  height: 8px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
`;

const SpeechBubble = styled(motion.div)`
  background: white;
  padding: 15px 20px;
  border-radius: 20px;
  max-width: 250px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  margin-bottom: 130px;
  font-size: 15px;
  line-height: 1.5;
  color: #000;
  font-weight: 600;
  text-align: left;
  z-index: 1001;
  transform: translateY(-50%);
  left: 100px;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    right: 20px;
    width: 20px;
    height: 20px;
    background: white;
    transform: rotate(45deg);
  }
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #4395CA;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 1002;

  &:hover {
    background: #357abd;
  }
`;

const Hero = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const images = [
    '/images/hero-1.png',
    '/images/hero-2.png',
    '/images/hero-3.png',
    '/images/hero-4.png'
  ];

  const [isVisible, setIsVisible] = useState(true);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentSection, setCurrentSection] = useState('hero');
  const owlRef = useRef(null);

  // Definir mensajes para cada sección
  const sectionMessages = {
    hero: "¡Hola! Soy tu asistente virtual. Te doy la bienvenida a PedLite",
    features: "¡Descubre todas las características increíbles que ofrece PedLite!",
    manageContent: "¡Maneja tu contenido de manera fácil y eficiente!",
    testimonials: "¡Los testimonios de nuestros usuarios son increíbles!",
    gallery: "¡Explora nuestra galería de imágenes!",
    download: "¡Descarga nuestra aplicación y comienza a aprender de una manera divertida!",
    contact: "¿Tienes alguna pregunta? ¡Estamos aquí para ayudarte!",
    about: "Conoce más sobre nuestra misión y el equipo detrás de PedLite"
  };

  // Función para detectar la sección actual
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section');
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          const sectionId = section.id;
          setCurrentSection(sectionId);
          setIsVisible(true); // Mostrar el mensaje cuando cambia la sección
          
          // Ocultar el mensaje después de 5 segundos
          const timer = setTimeout(() => {
            setIsVisible(false);
          }, 5000);

          return () => clearTimeout(timer);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 10000);

    const handleMouseMove = (e) => {
      if (owlRef.current) {
        const rect = owlRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setMousePosition({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      clearInterval(timer);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleCharacterClick = () => {
    setIsVisible(true);
    // Ocultar el mensaje después de 5 segundos cuando se hace clic
    setTimeout(() => {
      setIsVisible(false);
    }, 50000);
  };

  const calculatePupilPosition = (eyeX, eyeY) => {
    const dx = mousePosition.x - eyeX;
    const dy = mousePosition.y - eyeY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const maxDistance = 5;
    const angle = Math.atan2(dy, dx);
    const x = Math.cos(angle) * Math.min(distance, maxDistance);
    const y = Math.sin(angle) * Math.min(distance, maxDistance);
    return { x, y };
  };

  return (
    <HeroSection id="hero">
      <FloatingElements section="hero" />
      <BackgroundSlider>
        <AnimatePresence mode="wait">
          <BackgroundImage
            key={currentImage}
            image={images[currentImage]}
            initial={{ scale: 1.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{
              duration: 0.7,
              ease: "easeInOut"
            }}
          />
        </AnimatePresence>
      </BackgroundSlider>

      <Content>
        <Title
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          BIENVENIDO A PEDLITE
        </Title>
        <Subtitle
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          La plataforma educativa que revoluciona el aprendizaje
        </Subtitle>
      </Content>

      <SliderDots>
        {images.map((_, index) => (
          <Dot
            key={index}
            active={currentImage === index}
            onClick={() => setCurrentImage(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </SliderDots>

      <HeroContainer>
        <AnimatePresence>
          {isVisible && (
            <SpeechBubble
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <CloseButton
                onClick={handleClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                ✕
              </CloseButton>
              {sectionMessages[currentSection]}
            </SpeechBubble>
          )}
        </AnimatePresence>
        <HeroCharacter ref={owlRef} onClick={handleCharacterClick}>
          <OwlWings>
            <Wing />
            <Wing />
          </OwlWings>
          <OwlEars>
            <Ear />
            <Ear />
          </OwlEars>
          <OwlFace>
            <OwlEyes>
              <Eye>
                <Pupil
                  style={{
                    transform: `translate(calc(-50% + ${calculatePupilPosition(30, 30).x}px), calc(-50% + ${calculatePupilPosition(30, 30).y}px))`
                  }}
                />
              </Eye>
              <Eye>
                <Pupil
                  style={{
                    transform: `translate(calc(-50% + ${calculatePupilPosition(90, 30).x}px), calc(-50% + ${calculatePupilPosition(90, 30).y}px))`
                  }}
                />
              </Eye>
            </OwlEyes>
            <OwlBeak />
            <OwlCheeks>
              <Cheek />
              <Cheek />
            </OwlCheeks>
          </OwlFace>
        </HeroCharacter>
      </HeroContainer>
    </HeroSection>
  );
};

export default Hero; 